import type { ReactNode } from 'react'
import type { LogEntry } from '@/lib/server/research'

import { FadeLink } from '@/components/generals/fade-link'
import { Reveal, RevealLines } from '@/components/generals/reveal'
import { FieldChart } from '@/components/research/field-chart'
import { formatKey } from '@/lib/time'

interface ResearchStripProps {
    entries: LogEntry[]
}

const shortDate = (key: string) => {
    const [, month, day] = key.split('-').map(Number)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

    return `${day} ${months[month - 1]}.`
}

/** Three recent entries from the observatory logbook. */
export const ResearchStrip = ({ entries }: ResearchStripProps): ReactNode => {
    return (
        <section id='research-log' aria-labelledby='research-title' className='relative py-28 md:py-36'>
            <div className='container-page'>
                <div className='flex flex-wrap items-end justify-between gap-8'>
                    <div>
                        <p className='label-mono text-brass'>
                            Research log
                        </p>
                        <RevealLines
                            id='research-title'
                            lines={['Notes from', 'the night shift']}
                            className='display-serif mt-5 text-5xl text-star md:text-7xl'
                        />
                    </div>
                    <Reveal variant='wipe' className='max-w-md'>
                        <p className='text-star-2'>
                            When the visitors have gone home, the big telescope keeps working: timing eclipsing stars, catching exoplanets crossing their suns, and sending the results to the people who need them.
                        </p>
                    </Reveal>
                </div>

                {entries.length === 0 ? (
                    <p className='mt-16 text-star-2'>
                        The logbook is being updated. Please look again shortly.
                    </p>
                ) : (
                    <ol className='mt-16 grid gap-px bg-hair md:grid-cols-3'>
                        {entries.map((entry, index) => (
                            <Reveal as='li' key={entry.id} delay={index * 120} className='bg-ink'>
                                <article className='flex h-full flex-col p-6 md:p-8'>
                                    <div className='flex items-start justify-between gap-6'>
                                        <p className='font-heading text-3xl italic text-star'>
                                            {shortDate(entry.observedOn)}
                                        </p>
                                        <div className='w-20 shrink-0'>
                                            <FieldChart ra={entry.ra} dec={entry.dec} label={`Finder chart around ${entry.target}`} />
                                        </div>
                                    </div>
                                    <p className='label-mono mt-6 text-brass'>
                                        {entry.objectType}
                                    </p>
                                    <h3 className='mt-2 font-heading text-2xl text-star'>
                                        {entry.target}
                                    </h3>
                                    <p className='data-mono mt-2 text-star-3'>
                                        {`${entry.instrument}${entry.exposure ? ` · ${entry.exposure}` : ''}`}
                                    </p>
                                    <p className='mt-5 flex-1 border-t border-dashed border-hair-2 pt-5 text-[0.95rem] leading-relaxed text-star-2'>
                                        {entry.note}
                                    </p>
                                    <p className='label-mono mt-6 text-star-3'>
                                        {`${entry.observer} · ${formatKey(entry.observedOn, 'full')}`}
                                    </p>
                                </article>
                            </Reveal>
                        ))}
                    </ol>
                )}

                <FadeLink href='/research' className='group mt-10 inline-flex items-center gap-3 text-star'>
                    <span className='h-px w-8 bg-brass transition-all duration-500 group-hover:w-12' />
                    <span className='label-mono'>
                        The full log
                    </span>
                </FadeLink>
            </div>
        </section>
    )
}
